import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Users, UserPlus, Trash2, Eye, EyeOff, AlertCircle, CheckCircle, Shield, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5005';

const UserManagement = () => {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const { getThemeClasses } = useTheme();
  const themeClasses = getThemeClasses();

  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text: '' }

  // Create user form state
  const [newUser, setNewUser] = useState({ userId: '', email: '', password: '', role: 'viewer' });
  const [showPassword, setShowPassword] = useState(false);
  const [creating, setCreating] = useState(false);

  // Fetch users list
  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/auth/users`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setUsers(data.users || []);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreating(true);
    setMessage(null);

    try {
      const res = await fetch(`${API_URL}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(newUser),
      });

      const data = await res.json();

      if (!res.ok) {
        const errorMsg = data.details
          ? `${data.error}: ${data.details.join(', ')}`
          : data.message || data.error || 'Failed to create user';
        setMessage({ type: 'error', text: errorMsg });
        return;
      }

      setMessage({ type: 'success', text: `User "${newUser.userId}" created successfully!` });
      setNewUser({ userId: '', email: '', password: '', role: 'viewer' });
      setShowCreateForm(false);
      fetchUsers();
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (userId === currentUser?.userId) {
      setMessage({ type: 'error', text: 'You cannot delete your own account.' });
      return;
    }
    if (!window.confirm(`Are you sure you want to delete user "${userId}"?`)) return;

    try {
      const res = await fetch(`${API_URL}/api/auth/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });

      if (res.ok) {
        setMessage({ type: 'success', text: `User "${userId}" deleted.` });
        fetchUsers();
      } else {
        const data = await res.json();
        setMessage({ type: 'error', text: data.message || data.error || 'Failed to delete user' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.message });
    }
  };

  return (
    <div className={`min-h-screen ${themeClasses.primary} overflow-x-hidden`}>
      <div className="p-3 sm:p-4 md:p-6 space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className={`text-3xl font-bold ${themeClasses.textPrimary} mb-2`}>User Management</h1>
            <p className={themeClasses.textMuted}>Create and manage user accounts</p>
          </div>
          <button
            onClick={() => { setShowCreateForm(!showCreateForm); setMessage(null); }}
            className={`flex items-center gap-2 px-5 py-2.5 ${themeClasses.buttonPrimary} rounded-lg transition-colors`}
          >
            {showCreateForm ? <X size={18} /> : <UserPlus size={18} />}
            {showCreateForm ? 'Cancel' : 'Create User'}
          </button>
        </div>

        {/* Message */}
        {message && (
          <div className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${
            message.type === 'success'
              ? 'bg-green-500/10 border border-green-500/30 text-green-400'
              : 'bg-red-500/10 border border-red-500/30 text-red-400'
          }`}>
            {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            <span>{message.text}</span>
            <button onClick={() => setMessage(null)} className="ml-auto opacity-60 hover:opacity-100">
              <X size={14} />
            </button>
          </div>
        )}

        {/* Create User Form */}
        {showCreateForm && (
          <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6`}>
            <div className="flex items-center gap-3 mb-6">
              <UserPlus className={themeClasses.textAccent} size={24} />
              <h2 className={`text-xl font-semibold ${themeClasses.textPrimary}`}>Create New User</h2>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1.5`}>Username</label>
                  <input
                    type="text"
                    value={newUser.userId}
                    onChange={(e) => setNewUser({ ...newUser, userId: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg ${themeClasses.input} border ${themeClasses.borderInput} ${themeClasses.textPrimary} ${themeClasses.borderFocus} outline-none transition-colors`}
                    placeholder="e.g. john.doe"
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1.5`}>Email</label>
                  <input
                    type="email"
                    value={newUser.email}
                    onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg ${themeClasses.input} border ${themeClasses.borderInput} ${themeClasses.textPrimary} ${themeClasses.borderFocus} outline-none transition-colors`}
                    placeholder="john@example.com"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1.5`}>Password</label>
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={newUser.password}
                      onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                      className={`w-full px-4 py-2.5 pr-10 rounded-lg ${themeClasses.input} border ${themeClasses.borderInput} ${themeClasses.textPrimary} ${themeClasses.borderFocus} outline-none transition-colors`}
                      placeholder="Min 12 chars, mixed case, numbers, symbols"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className={`absolute right-3 top-1/2 -translate-y-1/2 ${themeClasses.textMuted}`}
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                  <p className={`text-xs ${themeClasses.textMuted} mt-1`}>
                    12+ chars, uppercase, lowercase, number, special char
                  </p>
                </div>
                <div>
                  <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-1.5`}>Role</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                    className={`w-full px-4 py-2.5 rounded-lg ${themeClasses.input} border ${themeClasses.borderInput} ${themeClasses.textPrimary} ${themeClasses.borderFocus} outline-none transition-colors`}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="operator">Operator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end pt-2">
                <button
                  type="submit"
                  disabled={creating}
                  className={`flex items-center gap-2 px-6 py-2.5 ${themeClasses.buttonPrimary} rounded-lg transition-colors ${creating ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {creating ? (
                    <>
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Creating...
                    </>
                  ) : (
                    <>
                      <UserPlus size={16} />
                      Create User
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Users List */}
        <div className={`${themeClasses.secondary} border ${themeClasses.border} rounded-xl p-6`}>
          <div className="flex items-center gap-3 mb-6">
            <Users className={themeClasses.textAccent} size={24} />
            <h2 className={`text-xl font-semibold ${themeClasses.textPrimary}`}>
              Users {!loadingUsers && `(${users.length})`}
            </h2>
          </div>

          {loadingUsers ? (
            <div className="flex justify-center py-8">
              <svg className="animate-spin h-8 w-8 text-military-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          ) : users.length === 0 ? (
            <p className={`text-center py-8 ${themeClasses.textMuted}`}>
              No users found. The user list endpoint may not be available yet.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className={`border-b ${themeClasses.border}`}>
                    <th className={`text-left py-3 px-4 text-sm font-medium ${themeClasses.textMuted}`}>User</th>
                    <th className={`text-left py-3 px-4 text-sm font-medium ${themeClasses.textMuted}`}>Email</th>
                    <th className={`text-left py-3 px-4 text-sm font-medium ${themeClasses.textMuted}`}>Role</th>
                    <th className={`text-left py-3 px-4 text-sm font-medium ${themeClasses.textMuted}`}>Status</th>
                    <th className={`text-left py-3 px-4 text-sm font-medium ${themeClasses.textMuted}`}>Last Login</th>
                    <th className={`text-right py-3 px-4 text-sm font-medium ${themeClasses.textMuted}`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.userId} className={`border-b ${themeClasses.border} last:border-0`}>
                      <td className={`py-3 px-4 ${themeClasses.textPrimary} font-medium`}>
                        <div className="flex items-center gap-2">
                          {u.role === 'admin' && <Shield size={14} className="text-yellow-500" />}
                          {u.userId}
                          {u.userId === currentUser?.userId && (
                            <span className={`text-xs ${themeClasses.textMuted}`}>(you)</span>
                          )}
                        </div>
                      </td>
                      <td className={`py-3 px-4 text-sm ${themeClasses.textSecondary}`}>{u.email}</td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          u.role === 'admin'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : u.role === 'operator'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-gray-500/20 text-gray-400'
                        }`}>
                          {u.role}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className={`inline-block px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          u.accountStatus === 'active'
                            ? 'bg-green-500/20 text-green-400'
                            : 'bg-red-500/20 text-red-400'
                        }`}>
                          {u.accountStatus}
                        </span>
                      </td>
                      <td className={`py-3 px-4 text-sm ${themeClasses.textMuted}`}>
                        {u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleDateString() : 'Never'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        {u.userId !== currentUser?.userId && (
                          <button
                            onClick={() => handleDeleteUser(u.userId)}
                            className="text-red-400 hover:text-red-300 p-1.5 rounded hover:bg-red-500/10 transition-colors"
                            title={`Delete ${u.userId}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserManagement;
